import { useMediaQuery } from '@mui/material';
import { NotFoundState } from '@rio-cloud/rio-uikit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { fetchAssetsConnectionStatus, fetchAssetsPlans, fetchComplementaryAssetsDetails } from '../../api/fetchAssets';
import { fetchAssetsByRioPlans } from '../../api/fetchBigNumbers';
import { fetchCheckupsByChassisList } from '../../api/fetchCheckup';
import { fetchMaintenance } from '../../api/fetchMaintenance';
import useAssetCampaign from '../../hooks/useAssetCampaign';
import useAssetGroupNames, { clearAssetGroupNamesCache } from '../../hooks/useAssetGroupNames';
import useAssets from '../../hooks/useAssets';
import useSessionValidity from '../../hooks/useSessionValidity';
import type { Asset } from '../../models/asset';
import { getAssetRioPlan } from '../../services/assetService';
import { exportAssetsToExcel } from '../../utils/exportAssetsToExcel';
import { calcProgress } from '../../utils/exportProgress';
import ContractDetailsSidebar from '../contractDetailsSidebar';
import InfoDialog from '../dialogs/InfoDialog';
import ExportProgressDialog, { type ExportStage } from '../exportProgressDialog';
import Inspections from '../inspections';
import MaintenanceGroupSidebar from '../maintenanceGroupSidebar';
import { UptimePager } from '../shared';
import useAssetAccounts, { clearAssetAccountsCache } from './hooks/useAssetAccounts';
import useMonitoringFilters from './hooks/useMonitoringFilters';
import useMonitoringIndicators from './hooks/useMonitoringIndicators';
import useMonitoringRows, { type BulkProgress } from './hooks/useMonitoringRows';
import usePageAlerts from './hooks/usePageAlerts';
import useVehicleAlerts from './hooks/useVehicleAlerts';
import useVehicleFullDetails from './hooks/useVehicleFullDetails';
import { hasNumber, type MonitoringRow } from './monitoringRow';
import {
    type ColumnKey,
    MARKETPLACE_URL,
    type MaintenanceStatus,
    type SearchField,
    type SortKey,
    type TileKey,
    VEHICLE_TYPES,
    getStatusPriority,
    toAssetSummaries,
} from './monitoringUtils';
import { type MonitoringPersona, getMonitoringPersonaConfig } from './personaConfig';
import { FLEET_LEVEL_SORTS, MONITORING_COLUMNS } from './ui/columns';
import FilterChips from './ui/FilterChips';
import MonitoringTable, { type IRowContext } from './ui/MonitoringTable';
import MonitoringToolbar from './ui/MonitoringToolbar';
import MoreFiltersDrawer from './ui/MoreFiltersDrawer';
import { normalizeMaintenanceGroup } from './ui/maintenanceGroupName';
import SummaryTiles from './ui/SummaryTiles';
import useFmText from './ui/useFmText';
import VehicleDetailDrawer, { type DetailTab } from './ui/VehicleDetailDrawer';
import '../shared/styles/tokens.scss';
import './fleetMonitoring.scss';

interface IMonitoringProps {
    persona: MonitoringPersona;
}

interface IBulkState extends BulkProgress {
    completedScope: Asset[] | null;
}

const FETCH_BLOCK_SIZE = 500;
const RESPONSIVE_QUERY = '(max-width: 1024px)';
const EXPORT_CHUNK_SIZE = 1000;
const EXPORT_CONCURRENCY = 4;

const TILE_KEYS: TileKey[] = ['agendar', 'atrasada', 'agendada', 'manut', 'conn1', 'conn0', 'vt1', 'vt0'];
const ALL_SEARCH_FIELDS: SearchField[] = ['all', 'placa', 'chassi', 'conta', 'grupo', 'nome'];
const IDLE_BULK: IBulkState = { done: 0, total: 0, completedScope: null };

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

const compareValues = (a: number | string | undefined, b: number | string | undefined, dir: 1 | -1) => {
    const aMissing = a === undefined || a === '';
    const bMissing = b === undefined || b === '';
    if (aMissing || bMissing) {
        return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
    }
    if (typeof a === 'string' || typeof b === 'string') {
        return collator.compare(String(a), String(b)) * dir;
    }
    return ((a as number) - (b as number)) * dir;
};

const plateOrChassis = (asset: Asset) => {
    const plate = asset.licensePlate?.trim();
    return plate && plate !== '-' ? plate : asset.chassis;
};

const fetchInChunks = async <T,>(ids: string[], request: (chunk: string[]) => Promise<T[]>) => {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += EXPORT_CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + EXPORT_CHUNK_SIZE));
    }
    const results: T[] = [];
    const worker = async () => {
        while (chunks.length) {
            const chunk = chunks.shift();
            if (chunk) {
                results.push(...(await request(chunk)));
            }
        }
    };
    await Promise.all(Array.from({ length: EXPORT_CONCURRENCY }, worker));
    return results;
};

const Monitoring = ({ persona }: IMonitoringProps) => {
    const navigate = useNavigate();
    const intl = useIntl();
    const { t, formatNumber } = useFmText();
    const config = useMemo(() => getMonitoringPersonaConfig(persona), [persona]);
    const isResponsiveView = useMediaQuery(RESPONSIVE_QUERY);
    const showExport = config.exportOnResponsive && isResponsiveView;
    const { allAssets, getAssets } = useAssets();
    const [assetsLoading, setAssetsLoading] = useState(() => !allAssets.length);

    useEffect(() => {
        if (allAssets.length) {
            setAssetsLoading(false);
            return;
        }
        getAssets().finally(() => setAssetsLoading(false));
    }, []);

    const { sessionValid, reportError } = useSessionValidity();
    const groupNames = useAssetGroupNames(sessionValid);
    const {
        accountByAsset,
        accounts,
        accountNameByKey,
        loading: accountsLoading,
    } = useAssetAccounts(allAssets, sessionValid && config.showAccounts);
    const {
        indicators,
        loading: indicatorsLoading,
        getStatus,
    } = useMonitoringIndicators(config.persona, allAssets, { enabled: sessionValid, assetsLoading });
    const { filters, update, setPage, toggleSort, reset, applyView, pageFiltersActive } = useMonitoringFilters();
    const { getRow, getAsset, isLoaded, load, reload, clear, failedIds, retryFailed } = useMonitoringRows(
        allAssets,
        reportError
    );
    const { handleCampaignsSidebar, handleCloseCampaignSidebar, campaignSidebar, campaigns, campaignsAssetId } =
        useAssetCampaign();

    const availableColumns = useMemo<ColumnKey[]>(
        () => MONITORING_COLUMNS.map(column => column.key).filter(key => key !== 'conta' || config.showAccounts),
        [config.showAccounts]
    );
    const searchFields = useMemo(
        () => ALL_SEARCH_FIELDS.filter(field => field !== 'conta' || config.showAccounts),
        [config.showAccounts]
    );
    const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
        () => new Set(MONITORING_COLUMNS.filter(column => column.defaultOn).map(column => column.key))
    );
    const shownColumns = useMemo(
        () => new Set([...visibleColumns].filter(key => availableColumns.includes(key))),
        [visibleColumns, availableColumns]
    );

    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState<{ tab: DetailTab; seq: number }>({ tab: 'summary', seq: 0 });
    const [maintenanceGroupAsset, setMaintenanceGroupAsset] = useState<Asset | null>(null);
    const [contractAsset, setContractAsset] = useState<Asset | null>(null);
    const [campaignsDialogCount, setCampaignsDialogCount] = useState<number | null>(null);
    const [bulk, setBulk] = useState<IBulkState>(IDLE_BULK);
    const resultsRef = useRef<HTMLDivElement>(null);
    const [exportState, setExportState] = useState<{
        open: boolean;
        stage: ExportStage;
        progress: number;
        detail?: string;
        error?: string;
    }>({ open: false, stage: 'maintenance', progress: 0 });

    useEffect(() => {
        if (sessionValid) {
            return;
        }
        clear();
        clearAssetAccountsCache();
        clearAssetGroupNamesCache();
        setSelectedAssetId(null);
        setContractAsset(null);
        setMaintenanceGroupAsset(null);
        setShowMoreFilters(false);
        setCampaignsDialogCount(null);
        setExportState(prev => ({ ...prev, open: false }));
        handleCloseCampaignSidebar();
    }, [sessionValid, clear]);

    const statusOf = useCallback(
        (row: MonitoringRow): MaintenanceStatus | undefined => getStatus(row.id) ?? row.nextMaintenance?.status,
        [getStatus]
    );

    const accountNameOf = useCallback(
        (row: MonitoringRow) => row.accountName ?? accountByAsset.get(row.id)?.name,
        [accountByAsset]
    );

    const searchIndex = useMemo(() => {
        const index = new Map<string, { placa: string; chassi: string; nome: string }>();
        for (const asset of allAssets) {
            index.set(asset.id, {
                placa: (asset.licensePlate ?? '').toLowerCase(),
                chassi: (asset.chassis ?? '').toLowerCase(),
                nome: (asset.name ?? '').toLowerCase(),
            });
        }
        return index;
    }, [allAssets]);

    const groupIndex = useMemo(() => {
        const index = new Map<string, string>();
        if (!groupNames.size) {
            return index;
        }
        for (const asset of allAssets) {
            const names = (asset.groupIds ?? []).map(groupId => groupNames.get(groupId) ?? '').join('\n');
            if (names.trim()) {
                index.set(asset.id, names.toLowerCase());
            }
        }
        return index;
    }, [allAssets, groupNames]);

    const term = filters.q.trim().toLowerCase();
    const usesAccounts =
        (!!term && (filters.qField === 'all' || filters.qField === 'conta')) || filters.accounts.length > 0;
    const accountLookup = usesAccounts ? accountByAsset : null;

    const baseAssets = useMemo(() => {
        const accountKeys = new Set(filters.accounts);
        const types = new Set(filters.types);
        const accountText = (id: string) => (accountLookup?.get(id)?.name ?? '').toLowerCase();
        const matchesTerm = (asset: Asset) => {
            const search = searchIndex.get(asset.id);
            if (!search) {
                return false;
            }
            switch (filters.qField) {
                case 'placa':
                case 'chassi':
                case 'nome':
                    return search[filters.qField].includes(term);
                case 'conta':
                    return accountText(asset.id).includes(term);
                case 'grupo':
                    return (groupIndex.get(asset.id) ?? '').includes(term);
                default:
                    return (
                        search.placa.includes(term) ||
                        search.chassi.includes(term) ||
                        search.nome.includes(term) ||
                        accountText(asset.id).includes(term) ||
                        (groupIndex.get(asset.id) ?? '').includes(term)
                    );
            }
        };
        return allAssets.filter(asset => {
            if (term && !matchesTerm(asset)) {
                return false;
            }
            if (types.size && !types.has(asset.type ?? '')) {
                return false;
            }
            if (accountKeys.size && !accountKeys.has(accountLookup?.get(asset.id)?.key ?? '')) {
                return false;
            }
            return true;
        });
    }, [allAssets, searchIndex, groupIndex, accountLookup, term, filters.qField, filters.types, filters.accounts]);

    const tileOf = useCallback(
        (tile: TileKey, assetId: string): boolean => {
            switch (tile) {
                case 'agendar':
                    return indicators.toSchedule.has(assetId);
                case 'atrasada':
                    return indicators.delayed.has(assetId);
                case 'agendada':
                    return indicators.scheduled.has(assetId);
                case 'manut':
                    return indicators.inMaintenance.has(assetId);
                case 'conn1':
                    return indicators.withService.has(assetId);
                case 'conn0':
                    return !indicators.withService.has(assetId);
                case 'vt1':
                    return indicators.withVolksTotal.has(assetId);
                case 'vt0':
                    return !indicators.withVolksTotal.has(assetId);
                default:
                    return true;
            }
        },
        [indicators]
    );

    const tileCounts = useMemo(() => {
        const counts = Object.fromEntries(TILE_KEYS.map(tile => [tile, 0])) as Record<TileKey, number>;
        for (const asset of baseAssets) {
            for (const tile of TILE_KEYS) {
                if (tileOf(tile, asset.id)) {
                    counts[tile] += 1;
                }
            }
        }
        return counts;
    }, [baseAssets, tileOf]);

    const scopeAssets = useMemo(
        () => (filters.tile ? baseAssets.filter(asset => tileOf(filters.tile as TileKey, asset.id)) : baseAssets),
        [baseAssets, filters.tile, tileOf]
    );

    const listReady = !assetsLoading;
    const needsDetails = pageFiltersActive || !FLEET_LEVEL_SORTS.includes(filters.sort.key);

    useEffect(() => {
        if (!needsDetails || !listReady || !sessionValid) {
            setBulk(prev => (prev === IDLE_BULK ? prev : IDLE_BULK));
            return;
        }
        let cancelled = false;
        const scope = scopeAssets;
        setBulk({ done: 0, total: scope.length, completedScope: null });
        load(
            scope.map(asset => asset.id),
            {
                isCancelled: () => cancelled,
                onProgress: progress => {
                    if (!cancelled) {
                        setBulk({ ...progress, completedScope: null });
                    }
                },
            }
        ).then(() => {
            if (!cancelled) {
                setBulk(prev => ({ ...prev, completedScope: scope }));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [needsDetails, listReady, sessionValid, scopeAssets, load]);

    const detailsReady = !needsDetails || bulk.completedScope === scopeAssets;
    const useFleetOrder = !needsDetails || (!detailsReady && !pageFiltersActive);

    const sortValue = useCallback(
        (key: SortKey, row: MonitoringRow): number | string | undefined => {
            switch (key) {
                case 'placa':
                    return row.licensePlate ?? row.chassis;
                case 'conta':
                    return accountNameOf(row);
                case 'status':
                    return getStatusPriority(statusOf(row));
                case 'prox':
                    return getStatusPriority(statusOf(row)) * 1e7 + (row.nextMaintenance?.km ?? 5e6);
                case 'ult': {
                    const number = row.lastMaintenance?.number;
                    if (!hasNumber(number)) {
                        return undefined;
                    }
                    return number * 1e13 + (row.lastMaintenance?.date?.getTime() ?? 0);
                }
                case 'odo':
                    return row.odometer?.value;
                case 'hor':
                    return row.hourmeter?.value;
                case 'garantia':
                    return row.warranty || undefined;
                case 'camp':
                    return row.campaignCount;
                case 'grupo':
                    return row.maintenanceGroup ? normalizeMaintenanceGroup(row.maintenanceGroup) : undefined;
                case 'conexao':
                    return row.loaded ? (row.deviceConnected ? 1 : 0) : undefined;
                default:
                    return undefined;
            }
        },
        [accountNameOf, statusOf]
    );

    const fleetOrderedAssets = useMemo(() => {
        if (!useFleetOrder) {
            return [];
        }
        const { key, dir } = filters.sort;
        const fleetKey: SortKey = FLEET_LEVEL_SORTS.includes(key) ? key : 'status';
        const fleetDir = FLEET_LEVEL_SORTS.includes(key) ? dir : 1;
        const fleetValue = (asset: Asset): number | string | undefined => {
            if (fleetKey === 'placa') {
                return plateOrChassis(asset);
            }
            if (fleetKey === 'conta') {
                return accountByAsset.get(asset.id)?.name;
            }
            return getStatusPriority(getStatus(asset.id));
        };
        return scopeAssets
            .map(asset => ({ asset, value: fleetValue(asset), plate: plateOrChassis(asset) }))
            .sort((a, b) => compareValues(a.value, b.value, fleetDir) || compareValues(a.plate, b.plate, 1))
            .map(item => item.asset);
    }, [useFleetOrder, filters.sort, scopeAssets, accountByAsset, getStatus]);

    const detailOrderedAssets = useMemo(() => {
        if (!needsDetails || !detailsReady) {
            return [];
        }
        const { key, dir } = filters.sort;
        const groups = new Set(filters.maintenanceGroups);
        return scopeAssets
            .map(asset => ({ asset, row: getRow(asset.id) }))
            .filter(({ row }) => {
                if (filters.warranty && row.warranty !== filters.warranty) {
                    return false;
                }
                if (filters.withCampaign && row.campaignCount <= 0) {
                    return false;
                }
                if (groups.size && !groups.has(normalizeMaintenanceGroup(row.maintenanceGroup))) {
                    return false;
                }
                return true;
            })
            .map(({ asset, row }) => ({ asset, value: sortValue(key, row), plate: row.licensePlate ?? row.chassis }))
            .sort((a, b) => compareValues(a.value, b.value, dir) || compareValues(a.plate, b.plate, 1))
            .map(item => item.asset);
    }, [
        needsDetails,
        detailsReady,
        filters.sort,
        filters.warranty,
        filters.withCampaign,
        filters.maintenanceGroups,
        scopeAssets,
        getRow,
        sortValue,
    ]);

    const orderedAssets = useFleetOrder ? fleetOrderedAssets : detailOrderedAssets;

    const totalPages = Math.max(1, Math.ceil(orderedAssets.length / filters.pageSize));
    const page = Math.min(filters.page, totalPages - 1);
    const pageAssets = useMemo(
        () => (listReady ? orderedAssets.slice(page * filters.pageSize, (page + 1) * filters.pageSize) : []),
        [listReady, orderedAssets, page, filters.pageSize]
    );
    const blockStart = Math.floor((page * filters.pageSize) / FETCH_BLOCK_SIZE) * FETCH_BLOCK_SIZE;
    const blockAssets = useMemo(
        () => (listReady ? orderedAssets.slice(blockStart, blockStart + FETCH_BLOCK_SIZE) : []),
        [listReady, orderedAssets, blockStart]
    );
    const nextBlockAssets = useMemo(
        () => (listReady ? orderedAssets.slice(blockStart + FETCH_BLOCK_SIZE, blockStart + FETCH_BLOCK_SIZE * 2) : []),
        [listReady, orderedAssets, blockStart]
    );

    const pageKey = pageAssets.map(asset => asset.id).join(',');
    const blockKey = `${blockStart}:${blockAssets.length}:${blockAssets[0]?.id ?? ''}:${blockAssets.at(-1)?.id ?? ''}`;

    useEffect(() => {
        if (!pageAssets.length || !sessionValid) {
            return;
        }
        let cancelled = false;
        const options = { isCancelled: () => cancelled };
        const run = async () => {
            await load(
                pageAssets.map(asset => asset.id),
                options
            );
            if (cancelled) {
                return;
            }
            await load(
                blockAssets.map(asset => asset.id),
                options
            );
            if (cancelled || !nextBlockAssets.length) {
                return;
            }
            await load(
                nextBlockAssets.map(asset => asset.id),
                options
            );
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [pageKey, blockKey, sessionValid, load]);

    const pageAssetIds = useMemo(() => pageAssets.map(asset => asset.id), [pageKey]);
    const { getAlertSummary } = usePageAlerts(pageAssetIds, sessionValid);

    const pageRows = useMemo(() => pageAssets.map(asset => getRow(asset.id)), [pageAssets, getRow]);

    const pageLoading = pageAssets.some(asset => !isLoaded(asset.id));
    const statusLoading = indicatorsLoading.maintenance || indicatorsLoading.delayed;
    const bulkLoading = needsDetails && !detailsReady;

    const getLoadingLabel = () => {
        if (assetsLoading) {
            return t('loading.vehicles');
        }
        if (bulkLoading) {
            return t('loading.bulk', {
                done: formatNumber(bulk.done),
                total: formatNumber(bulk.total || scopeAssets.length),
            });
        }
        if (pageLoading) {
            return t('loading.details');
        }
        if (statusLoading) {
            return t('loading.indicators');
        }
        return undefined;
    };
    const loadingLabel = getLoadingLabel();

    const hasAnyFilter =
        !!filters.q || !!filters.tile || filters.types.length > 0 || filters.accounts.length > 0 || pageFiltersActive;
    const resultCount = needsDetails && pageFiltersActive && !detailsReady ? undefined : orderedAssets.length;

    const context: IRowContext = useMemo(
        () => ({
            getStatus: statusOf,
            hasService: (row: MonitoringRow) => indicators.withService.has(row.id) || !!row.connectivity,
            hasVolksTotal: (row: MonitoringRow) => indicators.withVolksTotal.has(row.id) || !!row.volksTotal,
            getAccountName: accountNameOf,
        }),
        [statusOf, indicators, accountNameOf]
    );

    const vehicleTypeCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const asset of allAssets) {
            if (asset.type) {
                counts.set(asset.type, (counts.get(asset.type) ?? 0) + 1);
            }
        }
        const known = VEHICLE_TYPES.filter(type => counts.has(type));
        const others = [...counts.keys()].filter(type => !(VEHICLE_TYPES as readonly string[]).includes(type));
        return [...known, ...others].map(type => ({ type, count: counts.get(type) ?? 0 }));
    }, [allAssets]);

    const selectedRow = selectedAssetId ? getRow(selectedAssetId) : undefined;
    const selectedBaseAsset = selectedAssetId ? getAsset(selectedAssetId) : undefined;
    const { details: selectedFullAsset, loading: selectedFullLoading } = useVehicleFullDetails(selectedBaseAsset);
    const { alerts: selectedAlerts, loading: selectedAlertsLoading } = useVehicleAlerts(selectedAssetId);

    const handleExport = async () => {
        setExportState({ open: true, stage: 'maintenance', progress: 0 });
        try {
            const ids = orderedAssets.map(asset => asset.id);
            const [assetsPlans, assetsConnectionStatus] = await Promise.all([
                fetchInChunks(ids, fetchAssetsPlans),
                fetchInChunks(ids, fetchAssetsConnectionStatus),
            ]);
            await exportAssetsToExcel({
                allSummaryAssets: toAssetSummaries(orderedAssets),
                assets: [],
                assetsPlans,
                assetsConnectionStatus,
                fetchMaintenance,
                fetchComplementaryAssetsDetails,
                fetchCheckupsByChassisList,
                fetchAssetsByRioPlans,
                getAssetRioPlan,
                onChunk: (stage, index, total) => {
                    setExportState(prev => ({
                        ...prev,
                        stage,
                        detail: total ? `${index}/${total}` : undefined,
                        progress: calcProgress(stage, Math.min(index, total), total || 1),
                    }));
                },
            });
            setExportState(prev => ({ ...prev, stage: 'done', progress: 100, detail: undefined }));
        } catch (error: unknown) {
            setExportState(prev => ({
                ...prev,
                stage: 'error',
                error: error instanceof Error ? error.message : undefined,
            }));
        }
    };

    const schedule = (row: MonitoringRow) => {
        const params = new URLSearchParams({ asset: row.id, chassis: row.chassis, name: row.name ?? '' });
        navigate(`${config.basePath}/appointments/appointment?${params.toString()}`);
    };

    const changePage = (nextPage: number) => {
        setPage(nextPage);
        const results = resultsRef.current;
        if (!results) {
            return;
        }
        let scroller = results.parentElement;
        while (scroller && !/(auto|scroll)/.test(window.getComputedStyle(scroller).overflowY)) {
            scroller = scroller.parentElement;
        }
        const visibleTop = scroller?.getBoundingClientRect().top ?? 0;
        if (results.getBoundingClientRect().top < visibleTop) {
            results.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };

    const openVehicle = (row: MonitoringRow, tab: DetailTab = 'summary') => {
        setSelectedTab(prev => ({ tab, seq: prev.seq + 1 }));
        setSelectedAssetId(row.id);
    };

    const openFromDrawer = (action: () => void) => {
        setSelectedAssetId(null);
        action();
    };

    const moreFiltersCount = (filters.accounts.length ? 1 : 0) + (filters.maintenanceGroups.length ? 1 : 0);

    const tableRows = sessionValid ? pageRows : [];
    const showResults = sessionValid && listReady && resultCount !== undefined;

    return (
        <>
            <div className='fleet-monitoring'>
                <div className='fm-panel'>
                    <MonitoringToolbar
                        filters={filters}
                        onChange={update}
                        searchFields={searchFields}
                        vehicleTypeCounts={vehicleTypeCounts}
                        visibleColumns={shownColumns}
                        availableColumns={availableColumns}
                        onToggleColumn={key =>
                            setVisibleColumns(prev => {
                                const next = new Set(prev);
                                next.has(key) ? next.delete(key) : next.add(key);
                                return next;
                            })
                        }
                        moreFiltersCount={moreFiltersCount}
                        onOpenMoreFilters={() => setShowMoreFilters(true)}
                    />
                    <FilterChips
                        filters={filters}
                        onChange={update}
                        onReset={reset}
                        onApplyView={applyView}
                        accountNameByKey={accountNameByKey}
                    />
                </div>

                <SummaryTiles
                    counts={tileCounts}
                    loading={indicatorsLoading}
                    activeTile={filters.tile}
                    onSelect={tile => update({ tile })}
                />

                <div className='fm-panel fm-results' ref={resultsRef}>
                    <div className='fm-reshead'>
                        {showResults ? (
                            <strong>{t('results.count', { count: formatNumber(resultCount) })}</strong>
                        ) : (
                            sessionValid && <span className='fm-skel' style={{ width: 120, height: 18 }} />
                        )}
                        {filters.tile && <span className='fm-muted'>· {t(`tiles.${filters.tile}`)}</span>}
                        {sessionValid && failedIds.length > 0 && (
                            <output className='fm-loadfail'>
                                <span className='fm-pill fm-bad'>
                                    <span className='rioglyph rioglyph-warning-sign' />
                                    {t('results.loadFailed', { count: failedIds.length })}
                                </span>
                                <button type='button' className='fm-linkbtn' onClick={retryFailed}>
                                    {t('results.retry')}
                                </button>
                            </output>
                        )}
                        <span className='fm-spacer' />
                        {config.canCreateScheduling && (
                            <button
                                type='button'
                                className='fm-btn fm-primary'
                                onClick={() => navigate(`${config.basePath}/appointments/appointment`)}
                            >
                                <span className='rioglyph rioglyph-plus-sign' /> {t('actions.createScheduling')}
                            </button>
                        )}
                        {showExport && (
                            <button
                                type='button'
                                className='fm-btn'
                                disabled={!showResults || !orderedAssets.length}
                                onClick={handleExport}
                            >
                                {t('results.export')}
                            </button>
                        )}
                    </div>
                    <MonitoringTable
                        rows={tableRows}
                        visibleColumns={shownColumns}
                        sort={filters.sort}
                        onSort={toggleSort}
                        loading={sessionValid && !!loadingLabel}
                        loadingLabel={sessionValid ? loadingLabel : undefined}
                        emptyState={
                            sessionValid ? undefined : (
                                <NotFoundState
                                    icon='rioglyph-truck'
                                    headline={intl.formatMessage({ id: 'intl-msg:monitoring.noSelection.headline' })}
                                    message={intl.formatMessage({ id: 'intl-msg:monitoring.noSelection.message' })}
                                    outerClassName='border-none'
                                />
                            )
                        }
                        context={context}
                        getAlertSummary={getAlertSummary}
                        onOpen={openVehicle}
                        onSchedule={schedule}
                        onContract={() => window.open(MARKETPLACE_URL, '_blank')}
                        emptyMessage={hasAnyFilter ? t('results.empty') : t('results.emptyNoFilters')}
                    />
                    {showResults && (
                        <UptimePager
                            page={page}
                            pageSize={filters.pageSize}
                            total={orderedAssets.length}
                            onPageChange={changePage}
                            onPageSizeChange={pageSize => update({ pageSize })}
                            pageSizes={[25, 50, 100]}
                        />
                    )}
                </div>

                {showMoreFilters && (
                    <MoreFiltersDrawer
                        showAccounts={config.showAccounts}
                        accounts={accounts}
                        accountsLoading={accountsLoading}
                        selectedAccounts={filters.accounts}
                        selectedMaintenanceGroups={filters.maintenanceGroups}
                        onApply={(accountKeys, maintenanceGroups) => {
                            update({ accounts: accountKeys, maintenanceGroups });
                            setShowMoreFilters(false);
                        }}
                        onClose={() => setShowMoreFilters(false)}
                    />
                )}

                {selectedRow && (
                    <VehicleDetailDrawer
                        key={`${selectedRow.id}-${selectedTab.seq}`}
                        row={selectedRow}
                        initialTab={selectedTab.tab}
                        fullAsset={selectedFullAsset}
                        fullAssetLoading={selectedFullLoading}
                        context={context}
                        alerts={selectedAlerts}
                        alertsLoading={selectedAlertsLoading}
                        canEditMaintenanceGroup={config.canEditMaintenanceGroup}
                        onClose={() => setSelectedAssetId(null)}
                        onSchedule={schedule}
                        userRole={config.persona}
                        onOpenCampaigns={asset =>
                            config.campaignsAction === 'campaignsDialog'
                                ? setCampaignsDialogCount(selectedRow.campaignCount)
                                : handleCampaignsSidebar(asset)
                        }
                        onOpenContract={asset => openFromDrawer(() => setContractAsset(asset))}
                        onEditMaintenanceGroup={asset => openFromDrawer(() => setMaintenanceGroupAsset(asset))}
                        onMetricUpdated={assetId => reload([assetId])}
                    />
                )}
            </div>

            <div className='fm-legacy-layer'>
                {campaignSidebar && campaignsAssetId && (
                    <Inspections
                        inspectionsData={{ list: campaigns?.list }}
                        closeSidebar={handleCloseCampaignSidebar}
                        assetId={campaignsAssetId}
                        userRole={config.persona}
                    />
                )}

                <InfoDialog
                    show={campaignsDialogCount !== null}
                    title={t('campaignsDialog.title')}
                    body={t('campaignsDialog.body', { count: campaignsDialogCount ?? 0 })}
                    onClose={() => setCampaignsDialogCount(null)}
                />
            </div>

            <ContractDetailsSidebar
                asset={contractAsset}
                show={!!contractAsset}
                userRole={config.userRole}
                setMaintenanceGroupSidebarAsset={setMaintenanceGroupAsset}
                onClose={() => setContractAsset(null)}
            />

            <MaintenanceGroupSidebar
                asset={maintenanceGroupAsset}
                show={!!maintenanceGroupAsset}
                onClose={() => setMaintenanceGroupAsset(null)}
                onSuccess={() => {
                    if (maintenanceGroupAsset) {
                        reload([maintenanceGroupAsset.id]);
                    }
                }}
            />

            <ExportProgressDialog
                show={exportState.open}
                stage={exportState.stage}
                progress={exportState.progress}
                detail={exportState.detail}
                errorMessage={exportState.error}
                onClose={() => setExportState(prev => ({ ...prev, open: false }))}
            />
        </>
    );
};

export default Monitoring;
